/**
 * Local full-text search (docs/07 §6, docs/08 §10, REC-06): an FTS5 virtual table
 * `search_fts(block_id, title, body, tags_text, assets_text)` in the client SQLite.
 *
 * Changes reach the index in two steps:
 *
 * 1. SQLite triggers on the SDK's internal tables (`ps_data__blocks`, `ps_data__block_tags`) only
 *    record the affected block id in `search_dirty`. They fire for local writes (through the SDK
 *    views) and for rows applied by sync alike, and cost one small insert per row: writing FTS5
 *    from the triggers made the first sync of 50.000 blocks take minutes in the browser
 *    (docs/perf/f0-baseline.md §2.6).
 * 2. `flushSearchIndex` reindexes the dirty blocks in one set-based transaction, ordered by
 *    rowid (FTS5 flushes its pending buffer whenever a rowid goes backwards). `search` calls it
 *    first, so results are always current; the app also calls it after a sync.
 *
 * Tags: renaming or removing a tag (`ps_data__tags`) marks the blocks that carry it.
 *
 * `search_docs(block_id)` gives every block a stable integer rowid shared with `search_fts`, so
 * deletes go by rowid (deleting by a UNINDEXED column scans the whole index).
 *
 * - `ensureSearchIndex`: creates the tables and triggers; rebuilds when `SEARCH_VERSION` changes.
 * - `rebuildSearchIndex`: full rebuild from the local blocks.
 * - `search`: prefix search, case- and accent-insensitive, with a highlighted snippet.
 *
 * Tokenizer `unicode61 remove_diacritics 2` (folds accents on both sides). `assets_text` stays
 * empty until F4. Confirmed in F0 with the wa-sqlite build of @powersync/web 2.3.1 (Chrome,
 * WebKit, Safari iOS) and the SQLite of better-sqlite3 (Node tests).
 */
export const SEARCH_VERSION = 4;
/** Internal tables of the PowerSync SDK the triggers watch (checked by the tests). */
export const SDK_TABLES = {
    blocks: 'ps_data__blocks',
    blockTags: 'ps_data__block_tags',
    tags: 'ps_data__tags',
};
/**
 * The SDK indexes its JSON columns as `CAST(json_extract(data, '$.col') AS TEXT)`; the predicates
 * below use the same expression so that SQLite uses `ps_data__block_tags__by_block_taxonomy`
 * instead of scanning the table for every block.
 */
const COL = (rowAlias, column) => `CAST(json_extract(${rowAlias}.data, '$.${column}') AS TEXT)`;
const TAGS_TEXT = (blockIdExpr) => `(
  SELECT group_concat(json_extract(t.data, '$.name'), ' ')
  FROM ${SDK_TABLES.blockTags} bt
  JOIN ${SDK_TABLES.tags} t ON t.id = ${COL('bt', 'tag_id')}
  WHERE ${COL('bt', 'block_id')} = ${blockIdExpr}
)`;
/**
 * The snippet marks its matches with two control characters (f1-trash-search, design D5): the
 * index drops them from the text, so in a snippet they can only be FTS5's own marks.
 */
const MARK_START = '\u0002';
const MARK_END = '\u0003';
const CLEAN = (expr) => `replace(replace(${expr}, char(2), ''), char(3), '')`;
/** Indexes the live blocks selected by `where` (which may refer to `b` and `d`), by rowid. */
const INDEX_ROWS = (where) => `
  INSERT INTO search_fts (rowid, block_id, title, body, tags_text, assets_text)
    SELECT d.rowid, b.id,
           ${CLEAN(`COALESCE(json_extract(b.data, '$.title'), '')`)},
           ${CLEAN(`COALESCE(json_extract(b.data, '$.body'), '')`)},
           ${CLEAN(`COALESCE(${TAGS_TEXT('b.id')}, '')`)},
           ''
    FROM ${SDK_TABLES.blocks} b JOIN search_docs d ON d.block_id = b.id
    WHERE json_extract(b.data, '$.deleted_at') IS NULL AND ${where}
    ORDER BY d.rowid`;
/** DDL of the index (tables, virtual table and triggers), in order; exported for diagnostics. */
export const SEARCH_STATEMENTS = [
    `CREATE TABLE IF NOT EXISTS search_meta (key TEXT PRIMARY KEY, value TEXT)`,
    `CREATE TABLE IF NOT EXISTS search_docs (block_id TEXT PRIMARY KEY)`,
    `CREATE TABLE IF NOT EXISTS search_dirty (block_id TEXT PRIMARY KEY)`,
    `CREATE VIRTUAL TABLE IF NOT EXISTS search_fts USING fts5(
     block_id UNINDEXED, title, body, tags_text, assets_text,
     tokenize = 'unicode61 remove_diacritics 2'
   )`,
    // blocks: insert / update / delete → mark dirty
    ...['INSERT', 'UPDATE', 'DELETE'].map((event) => `CREATE TRIGGER IF NOT EXISTS flow_search_blocks_${event.toLowerCase()}
   AFTER ${event} ON ${SDK_TABLES.blocks}
   BEGIN
     INSERT OR IGNORE INTO search_dirty (block_id) VALUES (${event === 'DELETE' ? 'OLD' : 'NEW'}.id);
   END`),
    // block_tags: insert / update / delete → mark the block dirty
    ...['INSERT', 'UPDATE', 'DELETE'].map((event) => `CREATE TRIGGER IF NOT EXISTS flow_search_block_tags_${event.toLowerCase()}
   AFTER ${event} ON ${SDK_TABLES.blockTags}
   BEGIN
     INSERT OR IGNORE INTO search_dirty (block_id)
       VALUES (${COL(event === 'DELETE' ? 'OLD' : 'NEW', 'block_id')});
   END`),
    // tags: a renamed or removed tag changes the tags_text of every block that carries it
    ...['UPDATE', 'DELETE'].map((event) => `CREATE TRIGGER IF NOT EXISTS flow_search_tags_${event.toLowerCase()}
   AFTER ${event} ON ${SDK_TABLES.tags}
   BEGIN
     INSERT OR IGNORE INTO search_dirty (block_id)
       SELECT ${COL('bt', 'block_id')} FROM ${SDK_TABLES.blockTags} bt
       WHERE ${COL('bt', 'tag_id')} = ${event === 'DELETE' ? 'OLD' : 'NEW'}.id;
   END`),
];
/** True when FTS5 is compiled into the SQLite of this database. */
export async function fts5Available(db) {
    try {
        const rows = await db.getAll("SELECT sqlite_compileoption_used('ENABLE_FTS5') AS ok");
        if (rows[0]?.ok === 1)
            return true;
        // Some builds do not report compile options: try to use the module.
        await db.writeTransaction(async (tx) => {
            await tx.execute('CREATE VIRTUAL TABLE IF NOT EXISTS flow_fts5_probe USING fts5(a)');
            await tx.execute('DROP TABLE flow_fts5_probe');
        });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Creates the index and its triggers if missing. When the stored version differs (or there is
 * none) it drops whatever an earlier version left (tables and `flow_search_*` triggers),
 * recreates everything and rebuilds. Returns whether a rebuild happened.
 */
export async function ensureSearchIndex(db) {
    const create = async (tx) => {
        for (const sql of SEARCH_STATEMENTS)
            await tx.execute(sql);
    };
    await db.writeTransaction(create);
    const stored = await db.getOptional("SELECT value FROM search_meta WHERE key = 'version'");
    if (stored && Number(stored.value) === SEARCH_VERSION)
        return { rebuilt: false, version: SEARCH_VERSION };
    await db.writeTransaction(async (tx) => {
        const triggers = await tx.getAll("SELECT name FROM sqlite_master WHERE type = 'trigger' AND name LIKE 'flow_search_%'");
        for (const { name } of triggers)
            await tx.execute(`DROP TRIGGER IF EXISTS "${name}"`);
        for (const table of ['search_fts', 'search_docs', 'search_dirty', 'search_meta'])
            await tx.execute(`DROP TABLE IF EXISTS ${table}`);
        await create(tx);
    });
    await rebuildSearchIndex(db);
    return { rebuilt: true, version: SEARCH_VERSION };
}
/** Rebuilds the whole index from the local blocks. Returns the number of indexed blocks. */
export async function rebuildSearchIndex(db) {
    return db.writeTransaction(async (tx) => {
        await tx.execute('DELETE FROM search_fts');
        await tx.execute('DELETE FROM search_dirty');
        await tx.execute(`INSERT OR IGNORE INTO search_docs (block_id) SELECT id FROM ${SDK_TABLES.blocks}`);
        await tx.execute(INDEX_ROWS('1'));
        await tx.execute("INSERT OR REPLACE INTO search_meta (key, value) VALUES ('version', ?)", [
            String(SEARCH_VERSION),
        ]);
        const row = await tx.get('SELECT count(*) AS n FROM search_fts');
        return row.n;
    });
}
/**
 * Empties the index (sign-out, docs/10): `disconnectAndClear` of the SDK only clears its own
 * tables, so the indexed titles and bodies would otherwise stay in the local file.
 */
export async function clearSearchIndex(db) {
    await db.writeTransaction(async (tx) => {
        for (const table of ['search_fts', 'search_docs', 'search_dirty', 'search_meta']) {
            const exists = await tx.getOptional("SELECT name FROM sqlite_master WHERE name = ? AND type IN ('table', 'view')", [table]);
            if (exists)
                await tx.execute(`DELETE FROM ${table}`);
        }
    });
}
/** Number of blocks changed since the index was last flushed. */
export async function searchDirtyCount(db) {
    const row = await db.getOptional('SELECT count(*) AS n FROM search_dirty');
    return row?.n ?? 0;
}
/**
 * Reindexes the blocks recorded by the triggers (one set-based transaction, ordered by rowid)
 * and empties the queue. Returns how many blocks were processed. Cheap when nothing changed.
 */
export async function flushSearchIndex(db) {
    if ((await searchDirtyCount(db)) === 0)
        return 0;
    return db.writeTransaction(async (tx) => {
        const row = await tx.get('SELECT count(*) AS n FROM search_dirty');
        await tx.execute(`INSERT OR IGNORE INTO search_docs (block_id) SELECT block_id FROM search_dirty`);
        await tx.execute(`DELETE FROM search_fts WHERE rowid IN (
         SELECT d.rowid FROM search_docs d JOIN search_dirty x ON x.block_id = d.block_id
         ORDER BY d.rowid)`);
        await tx.execute(INDEX_ROWS('b.id IN (SELECT block_id FROM search_dirty)'));
        await tx.execute('DELETE FROM search_dirty');
        return row.n;
    });
}
/**
 * FTS5 query for what the user typed: every word becomes a quoted prefix term (`"pasap"*`),
 * words are AND-ed. Returns null when there is nothing to search.
 */
export function toFtsQuery(input) {
    const words = input
        .split(/\s+/)
        .map((w) => w.replace(/"/g, '').trim())
        .filter((w) => w.length > 0);
    if (words.length === 0)
        return null;
    return words.map((w) => `"${w}"*`).join(' ');
}
/** Splits a snippet marked with the control characters into its parts. */
export function snippetParts(raw) {
    const parts = [];
    let rest = raw;
    while (rest.length > 0) {
        const start = rest.indexOf(MARK_START);
        if (start < 0) {
            parts.push({ text: rest, match: false });
            break;
        }
        if (start > 0)
            parts.push({ text: rest.slice(0, start), match: false });
        const end = rest.indexOf(MARK_END, start + 1);
        const stop = end < 0 ? rest.length : end;
        const text = rest.slice(start + 1, stop);
        if (text.length > 0)
            parts.push({ text, match: true });
        rest = end < 0 ? '' : rest.slice(end + 1);
    }
    return parts;
}
/**
 * First page of results for the typed text (docs/12 §4: p95 < 150 ms with 50.000 blocks).
 * Flushes pending changes first, so the results reflect every local write and synced row.
 */
export async function search(db, input, limit = 20) {
    const query = toFtsQuery(input);
    if (!query)
        return [];
    await flushSearchIndex(db);
    const rows = await db.getAll(`SELECT f.block_id, f.title, b.kind, b.status, f.snippet, f.rank FROM (
       SELECT block_id, title,
              snippet(search_fts, -1, char(2), char(3), '…', 12) AS snippet,
              bm25(search_fts, 0.0, 10.0, 1.0, 5.0, 1.0) AS rank
       FROM search_fts WHERE search_fts MATCH ?
       ORDER BY rank LIMIT ?
     ) f JOIN blocks b ON b.id = f.block_id
     ORDER BY f.rank`, [query, limit]);
    return rows.map((row) => ({ ...row, snippet: snippetParts(row.snippet) }));
}
export async function searchIndexSize(db) {
    const rows = await db.getAll('SELECT count(*) AS n FROM search_fts');
    return rows[0]?.n ?? 0;
}
//# sourceMappingURL=index.js.map